/**
 * MJOS-219 — THE WINDOW ONTO THE STAGING DATABASE.
 *
 * Erez, this evening: *"how are we actually seeing it happening?"* He could not,
 * and that was a sequencing mistake — the viewer sat at item 14 of my own
 * checklist, behind fifty more files. Hours of work he would have had to take on
 * trust, from an agent that had already been wrong twice today in exactly the
 * way trust cannot catch. So it moved ahead of file two, and every file now
 * appears here as it lands.
 *
 * Four panels, in the order the argument runs:
 *
 *   1 · SOURCES     five systems, and the honest have-access column. This is
 *                   the panel that stops "we audited their data" being said as
 *                   though it meant all of it — we hold two of five.
 *   2 · FILES       51 rows. `landed` is cheap; `read` is the work, so the bar
 *                   measures parts READ rather than files copied.
 *   3 · UNKNOWNS    ⭐ the primary output. An empty panel here does not mean a
 *                   clean corpus, it means the work was hidden.
 *   4 · OBSERVATIONS every atom with the cell it came from and the REASONING
 *                   behind it. This is the acceptance test made clickable:
 *                   point at a number, get file · sheet · cell.
 *
 * Plain React over our own /api, deliberately, rather than an embedded database
 * tool: it is the dashboard's Structured render path, it needs no iframe auth
 * dance, and a third-party viewer would show the tables without showing the one
 * column that matters — why a human decided what they decided.
 */
// @refresh reset
import { useEffect, useState } from "react";
import { useApi } from "@/lib/api";

const MONO = "ui-monospace, SFMono-Regular, Menlo, monospace";
const SANS = "'Inter Tight', Inter, system-ui, -apple-system, sans-serif";

type Tally = {
  docs: number; parts: number; parts_classified: number; observations: number;
  needs_review: number; conflicts: number; unknowns: number; docs_mapped: number;
  model_changes: number; xrefs: number;
};
type Source = { code: string; name: string; master_for: string; have_access: boolean; evidence: string | null; note: string | null };
type Doc = { id: number; filename: string; kind: string; bytes: number; n_parts: number; read_parts: number; observations: number };
type Unknown_ = { id: number; filename: string | null; part: string | null; cell: string | null; question: string; what_we_saw: string | null; why_it_matters: string | null; who_can_answer: string | null; blocks: string | null };
type Obs = { id: number; filename: string; part: string | null; cell: string | null; label: string | null; raw: string | null; kind: string | null; status: string; confidence: string | null; reason: string | null };
type Payload = { sources: Source[]; docs: Doc[]; unknowns: Unknown_[]; mappings: unknown[]; samples: Obs[]; tally: Tally };

const card: React.CSSProperties = {
  background: "#FFFFFF", border: "1px solid #4A5A6B", borderRadius: 2, padding: 14,
};
const h: React.CSSProperties = {
  font: `600 11px/1.2 ${MONO}`, letterSpacing: ".08em", textTransform: "uppercase",
  color: "#5B6B7C", marginBottom: 8,
};

function Stat({ n, label, alarm }: { n: number; label: string; alarm?: boolean }) {
  return (
    <div style={{ ...card, minWidth: 118 }}>
      <div style={{ font: `700 26px/1 ${SANS}`, color: alarm ? "#B0463F" : "#2F3E4F" }}>{n.toLocaleString()}</div>
      <div style={{ font: `500 10px/1.3 ${MONO}`, color: "#5B6B7C", marginTop: 5, letterSpacing: ".04em" }}>{label}</div>
    </div>
  );
}

export function OutformStagingViewer() {
  const api = useApi();
  const [data, setData] = useState<Payload | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Polled, not fetched once. The run is happening WHILE he is looking at this,
  // so a snapshot would show a half-finished state as though it were the end.
  useEffect(() => {
    let dead = false;
    const load = async () => {
      try {
        const r = await api("/api/outform-staging");
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const j = (await r.json()) as Payload;
        if (!dead) { setData(j); setError(null); }
      } catch (e) {
        if (!dead) setError(String(e));
      }
    };
    load();
    const t = setInterval(load, 15000);
    return () => { dead = true; clearInterval(t); };
  }, [api]);

  if (error) return <div style={{ ...card, font: `13px ${SANS}`, color: "#B0463F" }}>staging API unreachable — {error}</div>;
  if (!data) return <div style={{ ...card, font: `13px ${SANS}`, color: "#5B6B7C" }}>reading the staging database…</div>;

  const t = data.tally;
  const held = data.sources.filter((s) => s.have_access).length;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18, width: 2360, font: SANS }}>

      {/* The numbers that say where the run actually is. `conflicts` and
          `unknowns` are drawn in the alarm colour ON PURPOSE — they are the
          output, not the failures, and a zero there is the thing to worry
          about. */}
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
        <Stat n={t.docs} label="files landed" />
        <Stat n={t.parts} label="parts (sheets + pages)" />
        <Stat n={t.docs_mapped} label="files read" />
        <Stat n={t.observations} label="observations" />
        <Stat n={t.needs_review} label="needs review" alarm />
        <Stat n={t.unknowns} label="UNKNOWNS ⭐" alarm />
        <Stat n={t.model_changes} label="model changes" />
        <Stat n={t.xrefs} label="golden ids" />
      </div>

      <div style={{ display: "flex", gap: 18, alignItems: "flex-start" }}>

        {/* 1 · SOURCES — the panel that keeps us honest about scope. */}
        <div style={{ ...card, width: 560 }}>
          <div style={h}>① source systems — we hold {held} of {data.sources.length}</div>
          {data.sources.map((s) => (
            <div key={s.code} style={{ padding: "8px 0", borderTop: "1px solid #EFF2F6" }}>
              <div style={{ display: "flex", gap: 8, alignItems: "baseline" }}>
                <span style={{ font: `700 12px ${MONO}`, color: s.have_access ? "#2F3E4F" : "#B0463F" }}>
                  {s.have_access ? "✓" : "✗"} {s.code}
                </span>
                <span style={{ font: `13px ${SANS}`, color: "#2F3E4F" }}>{s.name}</span>
              </div>
              <div style={{ font: `12px/1.45 ${SANS}`, color: "#5B6B7C", marginTop: 3 }}>
                master for <b style={{ color: "#2F3E4F" }}>{s.master_for}</b>
              </div>
              {s.note && <div style={{ font: `11.5px/1.45 ${SANS}`, color: "#B0463F", marginTop: 3 }}>{s.note}</div>}
            </div>
          ))}
        </div>

        {/* 2 · FILES — read, not landed. Landing is a copy; reading is the job. */}
        <div style={{ ...card, width: 700, maxHeight: 620, overflowY: "auto" }}>
          <div style={h}>② files — {data.docs.filter((d) => d.read_parts > 0).length} of {data.docs.length} read</div>
          {data.docs.map((d) => {
            const pct = d.n_parts ? Math.round((d.read_parts / d.n_parts) * 100) : 0;
            return (
              <div key={d.id} style={{ padding: "6px 0", borderTop: "1px solid #EFF2F6" }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                  <span style={{ font: `11.5px ${MONO}`, color: pct ? "#2F3E4F" : "#8A98A6" }}>{d.filename.slice(0, 58)}</span>
                  <span style={{ font: `11px ${MONO}`, color: "#5B6B7C", whiteSpace: "nowrap" }}>
                    {d.read_parts}/{d.n_parts} · {d.observations} obs
                  </span>
                </div>
                <div style={{ height: 3, background: "#EFF2F6", marginTop: 4 }}>
                  <div style={{ height: 3, width: `${pct}%`, background: "#4A5A6B" }} />
                </div>
              </div>
            );
          })}
        </div>

        {/* 3 · UNKNOWNS — ⭐ the primary output. */}
        <div style={{ ...card, width: 1020, maxHeight: 620, overflowY: "auto", borderColor: "#B0463F" }}>
          <div style={{ ...h, color: "#B0463F" }}>③ unknowns — the primary output ⭐</div>
          {data.unknowns.length === 0 ? (
            <div style={{ font: `13px/1.5 ${SANS}`, color: "#B0463F" }}>
              Empty. On a real corpus that does not mean it was clean — it means the work was hidden.
            </div>
          ) : data.unknowns.map((u) => (
            <div key={u.id} style={{ padding: "10px 0", borderTop: "1px solid #EFF2F6" }}>
              <div style={{ font: `600 13.5px/1.4 ${SANS}`, color: "#2F3E4F" }}>{u.question}</div>
              <div style={{ font: `11px ${MONO}`, color: "#5B6B7C", margin: "4px 0" }}>
                {u.filename?.slice(0, 46)}{u.part ? ` · ${u.part}` : ""}{u.cell ? ` · ${u.cell}` : ""}
              </div>
              {u.what_we_saw && <div style={{ font: `12.5px/1.5 ${SANS}`, color: "#5B6B7C" }}><b>Saw: </b>{u.what_we_saw}</div>}
              {u.why_it_matters && <div style={{ font: `12.5px/1.5 ${SANS}`, color: "#2F3E4F", marginTop: 3 }}><b>Matters: </b>{u.why_it_matters}</div>}
              <div style={{ font: `11px ${MONO}`, color: "#B0463F", marginTop: 4 }}>
                → {u.who_can_answer}{u.blocks ? `  ·  blocks ${u.blocks}` : ""}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 4 · OBSERVATIONS — the acceptance test, made clickable. Every atom
          carries its cell AND the reasoning, because at 2,000 folders these
          become rules, and a rule we cannot explain is one we cannot defend. */}
      <div style={{ ...card, maxHeight: 560, overflowY: "auto" }}>
        <div style={h}>④ observations — every number, with the cell it came from and why we read it that way</div>
        <table style={{ borderCollapse: "collapse", width: "100%", font: `12px ${SANS}` }}>
          <thead>
            <tr style={{ font: `600 10.5px ${MONO}`, color: "#5B6B7C", textAlign: "left" }}>
              {["file", "sheet", "cell", "label", "value", "kind", "status", "conf", "why we read it that way"].map((c) => (
                <th key={c} style={{ padding: "4px 8px", borderBottom: "1px solid #4A5A6B", whiteSpace: "nowrap" }}>{c}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.samples.map((o) => (
              <tr key={o.id} style={{ borderBottom: "1px solid #EFF2F6" }}>
                <td style={{ padding: "5px 8px", font: `11px ${MONO}`, color: "#5B6B7C" }}>{o.filename.slice(0, 26)}</td>
                <td style={{ padding: "5px 8px", font: `11px ${MONO}` }}>{o.part}</td>
                <td style={{ padding: "5px 8px", font: `700 11px ${MONO}`, color: "#2F3E4F" }}>{o.cell}</td>
                <td style={{ padding: "5px 8px", color: "#5B6B7C" }}>{o.label}</td>
                <td style={{ padding: "5px 8px", fontWeight: 600 }}>{o.raw}</td>
                <td style={{ padding: "5px 8px", font: `11px ${MONO}`, color: "#5B6B7C" }}>{o.kind}</td>
                <td style={{ padding: "5px 8px", font: `11px ${MONO}`, color: o.status === "confirmed" ? "#2F3E4F" : "#B0463F" }}>{o.status}</td>
                <td style={{ padding: "5px 8px", font: `11px ${MONO}`, color: "#5B6B7C" }}>{o.confidence}</td>
                <td style={{ padding: "5px 8px", color: "#5B6B7C", lineHeight: 1.45, minWidth: 420 }}>{o.reason}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
